import React, { useState } from 'react';
import axios from 'axios';
import { Gift, Mail, RefreshCw, Eye, Quote, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const FREQ = { "one-time": "Único", monthly: "Mensual", annual: "Anual" };
const EMAIL = { sent: { label: "Correo enviado", cls: "bg-brand-lightBlue text-brand-blue" }, failed: { label: "Correo falló", cls: "bg-red-50 text-red-600" }, no_recipient: { label: "Sin correo", cls: "bg-gray-100 text-gray-500" }, skipped: { label: "Correo pendiente", cls: "bg-yellow-50 text-yellow-700" } };

export const SentGifts = ({ gifts = [], onRefresh, onPreview }) => {
  const [resending, setResending] = useState(null);

  const resend = async (code) => {
    setResending(code);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/gifts/${code}/resend`);
      toast[res.data.email_status === "sent" ? "success" : "error"](res.data.email_status === "sent" ? "Correo reenviado" : "No se pudo reenviar el correo");
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al reenviar");
    } finally {
      setResending(null);
    }
  };

  return (
    <Card className="border-none shadow-md" data-testid="sent-gifts-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Gift className="w-5 h-5 text-brand-blue" /> Regalos enviados</CardTitle>
        <CardDescription>Sigue el estado de cada tarjeta de regalo que has generado.</CardDescription>
      </CardHeader>
      <CardContent>
        {gifts.length === 0 ? (
          <p className="text-sm text-gray-500" data-testid="sent-gifts-empty">Aún no has enviado regalos. Genera uno arriba y aparecerá aquí.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {gifts.map((g) => {
              const redeemed = g.status === "redeemed";
              const email = EMAIL[g.email_status] || EMAIL.no_recipient;
              return (
                <li key={g.id} data-testid={`sent-gift-${g.code}`} className="py-4 flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono font-bold tracking-widest text-brand-dark" data-testid={`sent-gift-code-${g.code}`}>{g.code}</span>
                      <Badge data-testid={`sent-gift-status-${g.code}`} className={`${redeemed ? "bg-brand-green text-brand-dark" : "bg-yellow-100 text-yellow-800"} hover:bg-inherit font-bold`}>{redeemed ? "Canjeado" : "Pendiente"}</Badge>
                      <Badge variant="secondary" className={`${email.cls} font-medium`}>{email.label}</Badge>
                    </div>
                    <p className="text-sm text-gray-600 mt-1 truncate"><Mail className="w-3.5 h-3.5 inline mr-1 text-gray-400" />{g.recipient_email || (g.recipient_phone ? `+${g.recipient_phone}` : "Sin destinatario")}{g.recipient_email && g.recipient_phone ? ` · +${g.recipient_phone}` : ""} · {FREQ[g.frequency] || g.frequency}{g.scheduled_date ? ` · ${g.scheduled_date}` : ""}</p>
                    {g.message && <p className="text-xs text-gray-500 italic mt-1 truncate"><Quote className="w-3 h-3 inline mr-1" />{g.message}</p>}
                    <p className="text-xs text-gray-400 mt-1">{new Date(g.created_at).toLocaleDateString()}{redeemed && g.redeemed_at ? ` · canjeado el ${new Date(g.redeemed_at).toLocaleDateString()}` : ""}</p>
                  </div>
                  <div className="flex items-center gap-3 md:gap-4">
                    <p className="text-xl font-extrabold text-brand-blue">${Number(g.amount).toLocaleString()}</p>
                    <Button variant="outline" size="sm" data-testid={`sent-gift-preview-${g.code}`} onClick={() => onPreview(g)} className="rounded-full h-8"><Eye className="w-3.5 h-3.5 mr-1" /> Ver</Button>
                    {!redeemed && g.whatsapp_url && (
                      <a href={g.whatsapp_url} target="_blank" rel="noreferrer" data-testid={`sent-gift-whatsapp-${g.code}`} className="inline-flex items-center h-8 px-3 rounded-full bg-[#25D366] hover:bg-[#1ebe5a] text-white text-sm font-bold transition-colors"><MessageCircle className="w-3.5 h-3.5 mr-1" /> WhatsApp</a>
                    )}
                    {!redeemed && g.recipient_email && (
                      <Button size="sm" data-testid={`sent-gift-resend-${g.code}`} onClick={() => resend(g.code)} disabled={resending === g.code} className="rounded-full h-8 bg-brand-blue text-white font-bold">
                        <RefreshCw className={`w-3.5 h-3.5 mr-1 ${resending === g.code ? "animate-spin" : ""}`} /> Reenviar
                      </Button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};
